"use client"

import {
  addContactForm,
  deleteContactForm,
  fetchContactFormId,
  fetchContactForms,
  updateContactForm,
} from "@/(admin)/modules/contact-form/services/contactFormService"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { useDomainStore } from "@/stores/use-domain-store"
import { PaginationMeta } from "@/types/event"
import {
  ContactFormData,
  ContactFormFieldType,
  ContactFormPayload
} from "@/types/types"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { generateDuplicateName } from "@/utils/generate-duplicate-name"
import { App, FormInstance } from "antd"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import queryString from "query-string"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface UseContactFormsOptions {
  /** Page size from "Configure view". */
  pageSize?: number
  /** Default sort attribute from "Configure view". */
  sortField?: string
  sortOrder?: "asc" | "desc"
}

export const useContactForms = (options: UseContactFormsOptions = {}) => {
  const { pageSize: viewPageSize = 20, sortField: viewSortField, sortOrder: viewSortOrder } = options
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { message, modal } = App.useApp()
  const { selectedDomain } = useDomainStore()
  const enforcedDomain = getEnforcedDomainValue(selectedDomain)
  const enforcedDomainRef = useRef(enforcedDomain)
  // Guards: reqSeq drops stale responses, inFlightRequestKeyRef collapses the
  // React 19 StrictMode double-invoke into one request.
  const reqSeq = useRef(0)
  const inFlightRequestKeyRef = useRef<string | null>(null)

  const [dataItem, setDataItem] = useState<ContactFormData[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [sortValues, setSortValues] = useState<{ [key: string]: string } | null>(null)
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: viewPageSize,
    total: 0,
    pageCount: 0,
  })

  // pageSize is owned by "Configure view" — reset to page 1 when it (or the sort)
  // changes. No "sync page size into pagination" effect any more (that fed the
  // double/loop fetch — RC1); the trigger effect reads viewPageSize directly.
  useEffect(() => {
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [viewPageSize, viewSortField, viewSortOrder])

  const { hasPermission } = useCheckPermission()

  useEffect(() => {
    enforcedDomainRef.current = enforcedDomain
  }, [enforcedDomain])

  const fetchContactFormsData = useCallback(
    async (params?: any) => {
      const requestParams = {
        ...params,
        ...(enforcedDomainRef.current ? { domain: enforcedDomainRef.current } : {}),
      }
      const requestKey = JSON.stringify(requestParams)
      if (inFlightRequestKeyRef.current === requestKey) {
        return
      }
      inFlightRequestKeyRef.current = requestKey
      const seq = ++reqSeq.current
      setLoading(true)
      try {
        const response = await fetchContactForms(requestParams)
        if (seq !== reqSeq.current) return // superseded by a newer request
        if (response && response.data) {
          setDataItem(response.data)
          // Only page/total/pageCount — pageSize stays owned by viewConfig (RC2).
          if (response.meta?.pagination) {
            const p = response.meta.pagination
            setPagination(prev => ({
              ...prev,
              page: p.page ?? prev.page,
              total: p.total ?? 0,
              pageCount: p.pageCount ?? 0,
            }))
          }
        } else {
          setDataItem([])
        }
      } catch (error) {
        message.error(t("Tải danh sách thất bại"))
      } finally {
        if (inFlightRequestKeyRef.current === requestKey) {
          inFlightRequestKeyRef.current = null
        }
        if (seq === reqSeq.current) setLoading(false)
      }
    },
    [message],
  )

  useEffect(() => {
    const parsedParams = queryString.parse(searchParams.toString())
    const newFilterValues: { [key: string]: string } = {}
    const newSortValues: { [key: string]: string } = {}

    const systemKeys = ["sort", "publicationState"]

    for (const key in parsedParams) {
      const value = parsedParams[key]
      if (typeof value === "string") {
        if (systemKeys.includes(key)) {
          newSortValues[key] = value
        } else if (key !== "page" && key !== "pageSize") {
          newFilterValues[key] = value
        }
      }
    }

    if (enforcedDomain) {
      newFilterValues.domain = enforcedDomain
    }

    setFilterValues(newFilterValues)
    setSortValues(newSortValues)
  }, [searchParams, enforcedDomain])

  useEffect(() => {
    if (filterValues === null && sortValues === null) return
    const params: Record<string, string> = {
      ...filterValues,
      ...sortValues,
      page: String(pagination.page),
      pageSize: String(viewPageSize),
    }
    // Apply the "Configure view" default sort when the URL has none.
    if (!params.sort && viewSortField) {
      params.sort = `${viewSortField}:${viewSortOrder ?? "desc"}`
    }
    fetchContactFormsData(params)
  }, [
    filterValues,
    sortValues,
    pagination.page,
    viewPageSize,
    fetchContactFormsData,
    needRefresh,
    viewSortField,
    viewSortOrder,
  ])

  const onSearch = useCallback(
    (filters: object) => {
      setPagination(prev => ({ ...prev, page: 1 }))
      const publicationState = searchParams.get("publicationState")
      const sort = searchParams.get("sort")
      const newQuery: any = { ...filters }
      if (enforcedDomain) newQuery.domain = enforcedDomain
      if (publicationState) newQuery.publicationState = publicationState
      if (sort) newQuery.sort = sort
      const searchString = queryString.stringify(newQuery, { skipEmptyString: true, skipNull: true })
      router.push(searchString ? `${pathname}?${searchString}` : pathname)
    },
    [pathname, router, searchParams, enforcedDomain],
  )

  const onTableChange = useCallback((paginationConfig: any, filters: any, sorter: any) => {
    setPagination(prev => ({
      ...prev,
      page: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }))

    if (sorter && !Array.isArray(sorter) && sorter.field && sorter.order) {
      const field = Array.isArray(sorter.field) ? sorter.field[sorter.field.length - 1] : sorter.field
      const order = sorter.order === "ascend" ? "asc" : "desc"
      const currentParams = queryString.parse(searchParams.toString())
      const searchString = queryString.stringify({ ...currentParams, sort: `${field}:${order}` })
      router.push(`${pathname}?${searchString}`)
    }
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [pathname, router, searchParams])

  const handleRefresh = useCallback((filterRef?: any) => {
    if (filterRef?.current) {
      filterRef.current.filterFunction()
    }
    const current = queryString.parse(searchParams.toString())
    const newParams: any = {}
    if (current.sort) newParams.sort = current.sort
    if (current.publicationState) newParams.publicationState = current.publicationState
    if (enforcedDomain) newParams.domain = enforcedDomain

    const searchString = queryString.stringify(newParams)
    router.push(searchString ? `${pathname}?${searchString}` : pathname)
    setPagination(prev => ({ ...prev, page: 1 }))
    setNeedRefresh(prev => !prev)
  }, [pathname, router, searchParams, enforcedDomain])

  const updateUrl = useCallback((newParams: Record<string, any>) => {
    const current = queryString.parse(searchParams.toString())
    const merged = { ...current, ...newParams, ...(enforcedDomain ? { domain: enforcedDomain } : {}) }
    const searchString = queryString.stringify(merged, { skipEmptyString: true, skipNull: true })
    router.push(`${pathname}?${searchString}`)
  }, [pathname, router, searchParams, enforcedDomain])

  const initialFilterValues = useMemo(() => {
    const params: Record<string, any> = {}
    searchParams.forEach((value, key) => {
      if (!["page", "pageSize", "sort", "publicationState"].includes(key)) {
        params[key] = value
      }
    })
    if (enforcedDomain) {
      params.domain = enforcedDomain
    }
    return params
  }, [searchParams, enforcedDomain])

  const fetchContactFormByIdAndSetForm = useCallback(
    async (id: number, form?: FormInstance) => {
      try {
        const response = await fetchContactFormId(id)
        if (!response || !response.attributes) {
          message.error(t("Không tìm thấy đơn liên hệ"))
          return { contactForm: null, formData: null }
        }
        const attr = response.attributes
        const formData: ContactFormFieldType = {
          name: attr.name || "",
          company: attr.company || "",
          phone: attr.phone || "",
          message: attr.message || "",
          email: attr.email || "",
          resolved: attr.resolved ?? false,
          domain: attr.domain || "",
        }
        if (form) form.setFieldsValue(formData)
        return { contactForm: response, formData }
      } catch (error) {
        message.error(t("Lỗi khi lấy đơn liên hệ"))
        return { contactForm: null, formData: null }
      }
    },
    [message],
  )

  const handleUpsertContactForm = useCallback(
    async (values: ContactFormFieldType, id?: number, status?: string) => {
      const payload: ContactFormPayload = {
        data: {
          ...values,
          publishedAt: status === "Published" ? new Date().toISOString() : null,
        },
      }
      try {
        if (id) {
          await updateContactForm(id, payload)
          message.success(t("Cập nhật form liên hệ thành công"))
          return { success: true, id }
        } else {
          const res = await addContactForm(payload)
          message.success(t("Tạo mới form liên hệ thành công"))
          return { success: true, id: res.data.id }
        }
      } catch {
        message.error(t("Lưu form liên hệ thất bại"))
        return { success: false }
      }
    },
    [message],
  )

  const handleDelete = useCallback(
    async (id: number, currentParams?: any) => {
      modal.confirm({
        title: t("Xác nhận xóa"),
        content: t("Bạn có chắc chắn muốn xóa form liên hệ này"),
        onOk: async () => {
          try {
            await deleteContactForm(id)
            fetchContactFormsData(currentParams)
            message.success(t("Xóa form liên hệ thành công"))
          } catch {
            message.error(t("Xóa thất bại"))
          }
        },
      })
    },
    [fetchContactFormsData, message],
  )

  const handleBulkDelete = useCallback(
    async (currentParams?: any) => {
      try {
        setLoading(true)
        const ids = selectedRowKeys.map(id => Number(id))
        await Promise.all(ids.map(id => deleteContactForm(id)))
        setSelectedRowKeys([])
        fetchContactFormsData(currentParams)
        message.success(t("Xóa nhiều form liên hệ thành công"))
      } catch {
        message.error(t("Xóa nhiều thất bại"))
      } finally {
        setLoading(false)
      }
    },
    [selectedRowKeys, fetchContactFormsData, message],
  )

  const handleApplyBulkAction = useCallback(
    (currentParams?: any) => {
      if (!hasPermission("contact-form", "delete")) {
        message.error(t("Bạn không có quyền thực hiện thao tác này"))
        return
      }
      if (bulkAction === "delete" && selectedRowKeys.length > 0) {
        modal.confirm({
          title: t("Xác nhận xóa"),
          content: t("Bạn có chắc chắn muốn xóa những mục đã chọn"),
          onOk: () => handleBulkDelete(currentParams),
        })
      } else {
        message.warning(t("Vui lòng chọn mục và hành động hợp lệ"))
      }
    },
    [hasPermission, bulkAction, selectedRowKeys, handleBulkDelete, message],
  )

  const handleDuplicate = useCallback(
    async (contactForm: ContactFormData, currentParams?: any) => {
      try {
        setLoading(true)
        const existingNames = dataItem.map(item => item.attributes.name).filter((name): name is string => !!name)
        const duplicatedData: ContactFormFieldType = {
          name: generateDuplicateName(contactForm.attributes.name || "", existingNames),
          company: contactForm.attributes.company || "",
          phone: contactForm.attributes.phone,
          message: contactForm.attributes.message || "",
          email: contactForm.attributes.email,
          resolved: contactForm.attributes.resolved ?? false,
          domain: contactForm.attributes.domain,
        }
        const result = await handleUpsertContactForm(duplicatedData)
        if (result.success) {
          fetchContactFormsData(currentParams)
          message.success(t("Nhân bản thành công"))
        }
      } catch {
        message.error(t("Nhân bản thất bại"))
      } finally {
        setLoading(false)
      }
    },
    [dataItem, handleUpsertContactForm, fetchContactFormsData, message],
  )

  return {
    dataItem,
    loading,
    pagination,
    selectedRowKeys,
    bulkAction,
    needRefresh,
    filterValues,
    initialFilterValues,
    actions: {
      fetchContactFormsData,
      fetchContactFormByIdAndSetForm,
      handleDelete,
      onSearch,
      onTableChange,
      handleUpsertContactForm,
      setSelectedRowKeys,
      setBulkAction,
      handleApplyBulkAction,
      handleRefresh,
      handleDuplicate,
      hasPermission,
      updateUrl,
    },
  }
}
